import { useEffect, useRef } from 'react';

import {
  buildStoreShippingAddressUpdatePayload,
  type IStoreShippingAddressFormValues,
} from './storeCheckoutShippingAddressUpdate.utils';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useUpdateStoreMutation } from '@/api/StoreQuery';

interface IStoreShippingAddressFormSnapshot extends IStoreShippingAddressFormValues {
  shippingOption?: string;
}

interface IUseStoreShippingAddressPersistenceParams {
  storeId: string | null;
  isReadOnly: boolean;
  getShippingFormValues: () => IStoreShippingAddressFormSnapshot;
}

const SHIPPING_ADDRESS_PERSIST_DEBOUNCE_MS = 1000;

export const useStoreShippingAddressPersistence = ({
  storeId,
  isReadOnly,
  getShippingFormValues,
}: IUseStoreShippingAddressPersistenceParams): {
  persistShippingAddress: (values?: IStoreShippingAddressFormValues) => void;
} => {
  const [updateStore] = useUpdateStoreMutation();
  const storeIdRef = useRef(storeId);
  const isReadOnlyRef = useRef(isReadOnly);
  const getShippingFormValuesRef = useRef(getShippingFormValues);

  useEffect(() => {
    storeIdRef.current = storeId;
  }, [storeId]);

  useEffect(() => {
    isReadOnlyRef.current = isReadOnly;
  }, [isReadOnly]);

  useEffect(() => {
    getShippingFormValuesRef.current = getShippingFormValues;
  }, [getShippingFormValues]);

  const persistShippingAddress = useDebouncedCallback(
    (values?: IStoreShippingAddressFormValues) => {
      if (!storeIdRef.current || isReadOnlyRef.current) {
        return;
      }

      const formSnapshot = getShippingFormValuesRef.current();

      if (formSnapshot.shippingOption !== 'one-place') {
        return;
      }

      const addressValues = values ?? formSnapshot;

      updateStore(buildStoreShippingAddressUpdatePayload(Number(storeIdRef.current), addressValues))
        .unwrap()
        .catch(() => {
          /* shipping address save is best-effort when store exists */
        });
    },
    SHIPPING_ADDRESS_PERSIST_DEBOUNCE_MS
  );

  return { persistShippingAddress };
};
